import {RefObject, useRef, useState} from "react";
import type {WebRtcClient} from "../../webrtc/WebRtcClient";
import type {StreamsType, UserStream} from "../AppContext";
import type {ConferenceUserWithStatus, UserLiveStatus} from "@inpriton/types/conferenceTypes";
import type {StreamType, TrackType} from "@inpriton/types/webrtcTypes";
import VolumeMeter from '../../helper/VolumeMeter'
import {isDisconnectRejection} from "../../ws/WsClient";

const useRemoteStreams = (
  webrtc: WebRtcClient,
  selfUuidRef: RefObject<string | null>,
  usersRef: RefObject<ConferenceUserWithStatus[]>,
) => {
  const [ streams, setStreams ] = useState<StreamsType>({})

  // RISKS.md/P1.25: the sole writer of `streams`, writing the ref synchronously so the next chain
  // link reads this commit before React renders it. No render-body mirror: a render carrying older
  // state would overwrite a newer write.
  const streamsRef = useRef<StreamsType>({})
  const commitStreams = (next: StreamsType) => {
    streamsRef.current = next
    setStreams(next)
  }

  // RISKS.md/P1.25: one chain per uuid, so a second update builds on the first's commit. Keep the
  // catch: a rejected link would stall every later update for that uuid.
  const streamChainsRef = useRef<{ [uuid: string]: Promise<void> }>({})
  const enqueueStreamsOp = (uuid: string, op: () => Promise<void> | void): Promise<void> => {
    const chains = streamChainsRef.current
    const run = (chains[uuid] ?? Promise.resolve()).then(op)
      .catch(e => console.warn('stream update failed:', uuid, e))
    chains[uuid] = run
    void run.then(() => { if (chains[uuid] === run) delete chains[uuid] })
    return run
  }

  // RISKS.md/P2.15: failed consumes, keyed `${uuid}/${trackId}`. A ref because the per-peer chain
  // writes it before React renders (RISKS.md/P1.25); the boolean is only what the notifier reads.
  const consumeFailuresRef = useRef<Set<string>>(new Set())
  const [ streamsFailed, setStreamsFailed ] = useState(false)
  const syncStreamsFailed = () => setStreamsFailed(consumeFailuresRef.current.size > 0)
  const forgetConsumeFailures = (uuid: string) => {
    consumeFailuresRef.current.forEach(key => {
      if (key.startsWith(`${uuid}/`)) consumeFailuresRef.current.delete(key)
    })
  }

  // RISKS.md/P1.6: nulling the meter leaves its nodes in the page-wide AudioContext until stop().
  const disposeStreamEntry = (entry: UserStream) => {
    entry.volumeMeter?.stop()
    entry.volumeMeter = null
    entry.stream.getTracks().forEach(track => {
      entry.stream.removeTrack(track)
      track.stop()
    })
    // RISKS.md/P1.13: stopping the track above does not close its consumer.
    Object.entries(entry.trackIds).forEach(([trackType, producerId]) =>
      webrtc.closeStream(producerId, trackType as TrackType))
    entry.trackIds = {}
  }

  // Chain links, never synchronous: an update in flight for the uuid would otherwise commit after
  // the removal and resurrect the entry.
  const removeStreams = (uuid: string) => enqueueStreamsOp(uuid, () => {
    // Before the early return: all of a peer's tracks can fail, leaving failures but no entry.
    forgetConsumeFailures(uuid)
    syncStreamsFailed()
    const { [uuid]: gone, ...rest } = streamsRef.current
    if (!gone) return
    Object.values(gone).forEach(disposeStreamEntry)
    commitStreams(rest)
  })

  const clearStreams = () => {
    // Pending chains too: an entry still in flight must be removed behind its own update.
    const uuids = new Set([...Object.keys(streamsRef.current), ...Object.keys(streamChainsRef.current)])
    uuids.forEach(uuid => { void removeStreams(uuid) })
  }

  // RISKS.md/P2.58: guarded here, not in the callers, so a new caller cannot consume itself.
  const updateStreams = (uuid: string, status: UserLiveStatus, fromSnapshot = false) => {
    if (uuid === selfUuidRef.current) return Promise.resolve()
    return enqueueStreamsOp(uuid, () => applyStreams(uuid, status, fromSnapshot))
  }

  // RISKS.md/P1.22: `fromSnapshot` marks a roster read (`conference:join` reply, `admitted`), not a
  // pushed `user:status`. A snapshot can be older than a push already applied, so it may only fill
  // in what we do not hold: removing and replacing belong to the push.
  const applyStreams = async (uuid: string, status: UserLiveStatus, fromSnapshot = false) => {
    // RISKS.md/P2.15: failed consumes never reached `trackIds`, so this run retries them all.
    forgetConsumeFailures(uuid)
    const userEntry = { ...(streamsRef.current[uuid] || {}) }

    // RISKS.md/P1.6: an empty device (`{camera: {}}`) counts as gone, not only a missing key. A
    // both-devices-off toggle sends one, and so can a closing tab's late retraction.
    const published = (deviceType: string) => Object.keys(status.streams[deviceType] ?? {}).length > 0
    if (!fromSnapshot) {
      for (const deviceType of Object.keys(userEntry)) {
        if (published(deviceType)) continue
        disposeStreamEntry(userEntry[deviceType])
        delete userEntry[deviceType]
      }
    }

    for (const [deviceType, tracks] of Object.entries(status.streams)) {
      if (!published(deviceType)) continue
      const previous = userEntry[deviceType]

      // Fresh entry AND fresh `trackIds` — both are mutated below, and `previous` is the object
      // committed state holds, so editing either in place would expose a half-built entry mid-await.
      const currentEntry: UserStream = previous
        ? { ...previous, trackIds: { ...previous.trackIds } }
        : {
          uuid: uuid,
          deviceType,
          stream: new MediaStream(),
          volumeMeter: null,
          trackIds: {},
          hasVideo: false,
          hasAudio: false,
        }

      // RISKS.md/P2.59 + P1.22: reconcile per track. Rebuilding the stream when video goes also
      // drops the untouched audio. A track survives while the status still names our id for it.
      const removed: Array<[string, string]> = fromSnapshot ? [] : Object.entries(currentEntry.trackIds)
        .filter(([trackType, oldId]) => tracks[trackType] !== oldId)
      const added = Object.entries(tracks)
        .filter(([trackType, trackId]) => fromSnapshot
          // A different id is a stale read, not a re-publish — consuming it chases a dead producer.
          ? typeof currentEntry.trackIds[trackType] === 'undefined'
          : currentEntry.trackIds[trackType] !== trackId)

      // RISKS.md/P2.33 + P1.22: catch per track, or one failed video discards the working audio.
      // A failed type stays out of `trackIds`, so the next status retries it.
      const consumed: Array<[string, string, MediaStreamTrack[]]> = []
      for (const [trackType, trackId] of added) {
        try {
          const remoteStream = await webrtc.getStream(trackId, deviceType as StreamType, trackType as TrackType)
          consumed.push([trackType, trackId, remoteStream.getTracks()])
        } catch (e) {
          console.warn('consume failed:', uuid, deviceType, trackType, trackId, e)
          if (!isDisconnectRejection(e)) consumeFailuresRef.current.add(`${uuid}/${trackId}`)
        }
      }

      const videoChanged = removed.some(([trackType]) => trackType === 'video')
        || consumed.some(([trackType]) => trackType === 'video')
      const audioChanged = removed.some(([trackType]) => trackType === 'audio')
        || consumed.some(([trackType]) => trackType === 'audio')

      if (removed.length > 0 || consumed.length > 0) {
        // RISKS.md/P2.60: survivors plus arrivals, so the old track outlives the consume replacing it.
        const previousTracks = currentEntry.stream.getTracks()
        const kept = previousTracks
          .filter(track => !removed.some(([trackType]) => track.kind === trackType))
        const arrived = consumed.flatMap(([, , trackList]) => trackList)

        if (videoChanged) {
          // A <video> does not reliably follow a swapped track on a stream it is already bound to,
          // so the picture needs a new object for VideoGrid's `userStream?.id` guard to re-attach.
          currentEntry.stream = new MediaStream([...kept, ...arrived])
        } else {
          // RISKS.md/P1.22: audio-only — mutate in place. Replacing the object makes VideoGrid
          // reassign `srcObject`, re-running the element's load algorithm and blacking the picture out.
          previousTracks
            .filter(track => !kept.includes(track))
            .forEach(track => currentEntry.stream.removeTrack(track))
          arrived.forEach(track => currentEntry.stream.addTrack(track))
        }

        for (const [trackType, oldId] of removed) {
          // Stopped here: closeStream no-ops once the cache is reset, leaving the track running.
          previousTracks.filter(track => track.kind === trackType).forEach(track => track.stop())
          webrtc.closeStream(oldId, trackType as TrackType)
          delete currentEntry.trackIds[trackType]
        }
        for (const [trackType, trackId] of consumed) {
          currentEntry.trackIds[trackType] = trackId
        }
      }

      // RISKS.md/P1.6 + P2.19: the meter's source node binds the first audio track and never
      // follows a swap, so rebuild it when that track changes. Key on the track, not the stream: a
      // video-only change would restart it for nothing.
      if (audioChanged && currentEntry.volumeMeter) {
        currentEntry.volumeMeter.stop()
        currentEntry.volumeMeter = null
      }
      if (currentEntry.stream.getAudioTracks().length) {
        if (!currentEntry.volumeMeter) {
          currentEntry.volumeMeter = new VolumeMeter()
          currentEntry.volumeMeter.setStream(currentEntry.stream)
        }
        currentEntry.hasAudio = true
      } else {
        currentEntry.volumeMeter?.stop()
        currentEntry.volumeMeter = null
        currentEntry.hasAudio = false
      }

      userEntry[deviceType] = currentEntry
    }

    const next = { ...streamsRef.current, [uuid]: userEntry }
    if (!Object.keys(userEntry).length) delete next[uuid]
    commitStreams(next)
    syncStreamsFailed()
  }

  // RISKS.md/P2.15: as a PUSH, not a snapshot. Both would re-add the failed tracks, but a snapshot
  // never removes, and `usersRef` holds the newest status, so stale tracks must go too.
  const retryStreams = () => {
    usersRef.current.forEach(user => {
      if (user.status) void updateStreams(user.uuid, user.status)
    })
  }

  return {
    streams, commitStreams, streamsFailed,
    updateStreams, removeStreams, clearStreams, retryStreams,
  }
}

export default useRemoteStreams
